
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface LanguagesSpokenSelectorProps {
  practitionerId: string;
  initialLanguages: string[] | null;
  onUpdate: () => void;
}

const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', nameCN: '英语' },
  { code: 'zh', name: 'Chinese', nameCN: '中文' },
  { code: 'es', name: 'Spanish', nameCN: '西班牙语' },
  { code: 'fr', name: 'French', nameCN: '法语' },
  { code: 'de', name: 'German', nameCN: '德语' },
  { code: 'ja', name: 'Japanese', nameCN: '日语' },
  { code: 'ko', name: 'Korean', nameCN: '韩语' },
  { code: 'pt', name: 'Portuguese', nameCN: '葡萄牙语' },
  { code: 'ru', name: 'Russian', nameCN: '俄语' },
  { code: 'ar', name: 'Arabic', nameCN: '阿拉伯语' },
  { code: 'hi', name: 'Hindi', nameCN: '印地语' },
  { code: 'it', name: 'Italian', nameCN: '意大利语' },
  { code: 'nl', name: 'Dutch', nameCN: '荷兰语' },
  { code: 'pl', name: 'Polish', nameCN: '波兰语' },
  { code: 'tr', name: 'Turkish', nameCN: '土耳其语' },
  { code: 'vi', name: 'Vietnamese', nameCN: '越南语' },
  { code: 'th', name: 'Thai', nameCN: '泰语' },
  { code: 'id', name: 'Indonesian', nameCN: '印尼语' },
  { code: 'ms', name: 'Malay', nameCN: '马来语' },
  { code: 'sv', name: 'Swedish', nameCN: '瑞典语' },
  { code: 'no', name: 'Norwegian', nameCN: '挪威语' },
  { code: 'da', name: 'Danish', nameCN: '丹麦语' },
  { code: 'fi', name: 'Finnish', nameCN: '芬兰语' },
  { code: 'el', name: 'Greek', nameCN: '希腊语' },
  { code: 'he', name: 'Hebrew', nameCN: '希伯来语' },
  { code: 'cs', name: 'Czech', nameCN: '捷克语' },
  { code: 'hu', name: 'Hungarian', nameCN: '匈牙利语' },
  { code: 'ro', name: 'Romanian', nameCN: '罗马尼亚语' },
  { code: 'uk', name: 'Ukrainian', nameCN: '乌克兰语' },
];

export default function LanguagesSpokenSelector({ practitionerId, initialLanguages, onUpdate }: LanguagesSpokenSelectorProps) {
  const { t, i18n } = useTranslation('dashboard');
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(initialLanguages || []);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleLanguageToggle = (langCode: string) => {
    setSelectedLanguages(prev => 
      prev.includes(langCode) 
        ? prev.filter(code => code !== langCode)
        : [...prev, langCode]
    );
  };

  const handleRemoveLanguage = (langCode: string) => {
    setSelectedLanguages(prev => prev.filter(code => code !== langCode));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedLanguages.length === 0) {
      toast({
        title: t('languagesSpoken.selectAtLeastOne'),
        description: t('languagesSpoken.selectAtLeastOneDescription'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('practitioners')
        .update({ 
          languages_spoken: selectedLanguages,
          updated_at: new Date().toISOString()
        })
        .eq('id', practitionerId);

      if (error) throw error;

      toast({
        title: t('languagesSpoken.success'),
        description: t('languagesSpoken.successDescription'),
      });

      onUpdate();
    } catch (error) {
      console.error('Error updating languages:', error);
      toast({
        title: t('languagesSpoken.error'),
        description: t('languagesSpoken.errorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedLangObjects = AVAILABLE_LANGUAGES.filter(lang => 
    selectedLanguages.includes(lang.code)
  );

  const filteredLanguages = React.useMemo(() => {
    if (!searchQuery.trim()) return AVAILABLE_LANGUAGES;
    const query = searchQuery.toLowerCase();
    return AVAILABLE_LANGUAGES.filter(lang => 
      lang.name.toLowerCase().includes(query) ||
      lang.nameCN.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('languagesSpoken.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('languagesSpoken.description')}
          </p>

          {/* Popover Dropdown */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between border-amber-200"
              >
                <span className="text-muted-foreground">
                  {t('languagesSpoken.searchPlaceholder')}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder={t('languagesSpoken.searchPlaceholder')}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList className="max-h-96">
                  <CommandEmpty>{t('languagesSpoken.noResults')}</CommandEmpty>
                  <CommandGroup>
                    {filteredLanguages.map(lang => (
                      <CommandItem
                        key={lang.code}
                        value={lang.name}
                        onSelect={() => {
                          handleLanguageToggle(lang.code);
                        }}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-2 w-full">
                          <Checkbox
                            checked={selectedLanguages.includes(lang.code)}
                            className="pointer-events-none"
                          />
                          <span className="flex-1">
                            {i18n.language === 'zh' ? lang.nameCN : lang.name}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Selected Languages Display */}
          {selectedLangObjects.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-amber-50/50 rounded-md border border-amber-100">
              {selectedLangObjects.map(lang => (
                <Badge 
                  key={lang.code} 
                  variant="secondary"
                  className="bg-amber-100 text-amber-900 hover:bg-amber-200"
                >
                  {i18n.language === 'zh' ? lang.nameCN : lang.name}
                  <button
                    type="button"
                    onClick={() => handleRemoveLanguage(lang.code)}
                    className="ml-1 hover:text-amber-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('languagesSpoken.savingChanges')}
              </>
            ) : (
              t('languagesSpoken.saveChanges')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
